import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CategoryImageUpload = ({ image, onChange, color, onColorChange }) => {
  const [dragOver, setDragOver] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const fileInputRef = useRef(null);

  const predefinedColors = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
    '#8B5CF6', '#06B6D4', '#84CC16', '#F97316',
    '#EC4899', '#6366F1', '#14B8A6', '#DC2626'
  ];

  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    // Validate file size (max 2MB for category images)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image file size must be less than 2MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageData = {
        file,
        url: e.target.result,
        name: file.name,
        size: file.size
      };
      onChange(imageData);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const removeImage = () => {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className="bg-white rounded-lg border border-border p-6 space-y-6">
      <div className="flex items-center mb-4">
        <Icon name="Image" size={20} className="text-primary mr-2" />
        <h3 className="text-lg font-semibold text-foreground">Visual Identity</h3>
      </div>

      {/* Image Upload Section */}
      <div>
        <label className="text-sm font-medium text-foreground mb-3 block">
          Category Image
        </label>

        {!image ? (
          <div
            className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragOver 
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            
            <div className="space-y-3">
              <Icon 
                name="Upload" 
                size={32} 
                className={`mx-auto ${dragOver ? 'text-primary' : 'text-muted-foreground'}`} 
              />
              <div>
                <p className="text-sm font-medium text-foreground">
                  {dragOver ? 'Drop image here' : 'Upload category image'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Drag & drop or click to browse
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative">
            <img
              src={image.url}
              alt={image.name}
              className="w-full h-32 object-cover rounded-lg border border-border"
            />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/10 rounded-lg transition-colors group">
              <Button
                variant="destructive"
                size="sm"
                onClick={removeImage}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                iconName="Trash2"
              >
                Remove
              </Button>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {image.name} ({formatFileSize(image.size)})
            </div>
          </div>
        )}

        <div className="text-xs text-muted-foreground mt-2 space-y-1">
          <p>• Recommended size: 400x300px or similar ratio</p>
          <p>• Supported formats: JPG, PNG, WebP</p>
          <p>• Maximum file size: 2MB</p>
        </div>
      </div>

      {/* Color Theme Section */}
      <div>
        <label className="text-sm font-medium text-foreground mb-3 block">
          Category Color Theme
        </label>

        <div className="space-y-3">
          {/* Current Color Display */}
          <div className="flex items-center space-x-3">
            <div 
              className="w-8 h-8 rounded-full border-2 border-white shadow-md cursor-pointer"
              style={{ backgroundColor: color }}
              onClick={() => setShowColorPicker(!showColorPicker)}
            />
            <div>
              <p className="text-sm font-medium text-foreground">{color}</p>
              <p className="text-xs text-muted-foreground">Category theme color</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowColorPicker(!showColorPicker)}
              iconName="Palette"
            >
              Change
            </Button>
          </div>

          {/* Color Picker */}
          {showColorPicker && (
            <div className="p-4 border border-border rounded-lg bg-muted/50">
              <div className="grid grid-cols-6 gap-2 mb-4">
                {predefinedColors.map((presetColor) => (
                  <button
                    key={presetColor}
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                      color === presetColor ? 'border-foreground' : 'border-white shadow-md'
                    }`}
                    style={{ backgroundColor: presetColor }}
                    onClick={() => {
                      onColorChange(presetColor);
                      setShowColorPicker(false);
                    }}
                  />
                ))}
              </div>
              
              {/* Custom Color Input */}
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => onColorChange(e.target.value)}
                  className="w-8 h-8 rounded border border-input cursor-pointer"
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => onColorChange(e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border border-input rounded-md bg-background"
                  placeholder="#000000"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowColorPicker(false)}
                  iconName="Check"
                >
                  Done
                </Button>
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            This color will be used for category identification and theming throughout the interface.
          </p>
        </div>
      </div>

      {/* Preview Section */}
      <div className="border-t pt-4">
        <label className="text-sm font-medium text-foreground mb-3 block">
          Preview
        </label>
        <div className="flex items-center space-x-3 p-3 border border-border rounded-lg bg-muted/20">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: color }}
          >
            {image ? (
              <img
                src={image.url}
                alt="Category preview"
                className="w-full h-full object-cover rounded-lg"
              />
            ) : (
              <Icon name="Tag" size={20} className="text-white" />
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">Category Preview</p>
            <p className="text-xs text-muted-foreground">How it will appear in the system</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryImageUpload;